import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Typography,
} from "@mui/material";
import { nacidRequests } from "api/nacidApi";
import permissions from "app/permissions";
import { getPermissions } from "auth/security/securityUtils";
import { useCallback, useEffect, useState } from "react";
import dataProvider from "server/dataProvider";
import { checkPermission } from "server/security/utils";
import { useNotifyError } from "utils/useNotifyError";
import RequestPayloadViewer from "./RequestPayloadViewer";

const statusColor = code => {
  if (code == null) return "default";
  if (code >= 200 && code < 300) return "success";
  if (code >= 400 && code < 500) return "warning";
  return "error";
};

const AttemptRow = ({ request, payload }) => (
  <Box>
    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1 }}>
      <Chip
        size="small"
        label={`Опит ${request.attemptNumber}`}
      />
      <Chip
        size="small"
        variant="outlined"
        label={request.httpMethod}
      />
      <Chip
        size="small"
        color={statusColor(request.responseStatusCode)}
        label={request.responseStatusCode ?? "без отговор"}
      />
      {request.durationMs != null ? (
        <Chip
          size="small"
          variant="outlined"
          label={`${request.durationMs} ms`}
        />
      ) : null}
      {/* Distinguishes "nothing sensitive was present" from "we masked something". */}
      {request.isRedacted ? (
        <Chip
          size="small"
          color="info"
          variant="outlined"
          label="Маскирани данни"
        />
      ) : null}
      {request.isBodySuppressed ? (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label="Тялото не се съхранява"
        />
      ) : null}
    </Box>

    <Typography
      variant="caption"
      sx={{ fontFamily: "monospace", wordBreak: "break-all", display: "block", mb: 1 }}
    >
      {request.endpoint}
    </Typography>

    {request.error ? (
      <Typography
        variant="body2"
        color="error"
        sx={{ mb: 1 }}
      >
        {request.error}
      </Typography>
    ) : null}

    {payload ? (
      <>
        <RequestPayloadViewer
          title="Тяло на заявката"
          body={payload.requestBody}
        />
        <RequestPayloadViewer
          title="Тяло на отговора"
          body={payload.responseBody}
        />
      </>
    ) : null}
  </Box>
);

// The attempts themselves come nested in the flow's getOne — one row per PHYSICAL call, so a resilience retry
// and an auth replay show as the separate calls they were. Only the request/response *bodies* need a second
// call, and they need a second permission: even redacted they carry ЕГН/ЛНЧ/ИДН.
const StepRequests = ({ flowId, step }) => {
  const notifyError = useNotifyError();
  const [payloadsById, setPayloadsById] = useState(null);
  const [wasOpened, setWasOpened] = useState(false);

  const requests = step.requests ?? [];
  const canSeePayloads = checkPermission(getPermissions(), permissions.canPreviewNacidRequestPayloads);

  const fetchPayloads = useCallback(async () => {
    try {
      const { data } = await dataProvider.customRequest(
        nacidRequests.getFlowStepRequests(flowId, step.id),
        null,
        "GET",
      );

      setPayloadsById(Object.fromEntries((data?.items ?? []).map(x => [x.id, x])));
    } catch (err) {
      notifyError(err);
      setPayloadsById({});
    }
  }, [flowId, step.id, notifyError]);

  useEffect(() => {
    // Once per step, on first expand — never on page load, or a flow with many steps would fan out into a
    // request per step before the operator has looked at any of them.
    if (!wasOpened || !canSeePayloads || payloadsById != null) return;

    fetchPayloads();
  }, [wasOpened, canSeePayloads, payloadsById, fetchPayloads]);

  if (requests.length === 0) return null;

  return (
    <Accordion
      disableGutters
      onChange={(_, expanded) => expanded && setWasOpened(true)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2">{step.requestsCount ?? requests.length} HTTP заявки</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {canSeePayloads && payloadsById == null ? (
          <Typography
            variant="body2"
            sx={{ mb: 2 }}
          >
            Зареждане на телата…
          </Typography>
        ) : null}

        {requests.map((request, index) => (
          <Box key={request.id}>
            {index > 0 ? <Divider sx={{ my: 2 }} /> : null}
            <AttemptRow
              request={request}
              payload={payloadsById?.[request.id]}
            />
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

export default StepRequests;
